import type { ReactNode } from "react";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@altimateai/lego";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional caption rendered as an info-icon tooltip beside the title, so a
   *  chart's data caveat survives enlarging — the dialog mounts only the title
   *  and the chart, not any sibling the page drew next to it. */
  info?: string;
  children: ReactNode;
}

/** The enlarge view for a `Chart`: the same chart re-rendered at full size,
 *  with the chart's title (and optional caption) in the dialog header. */
const ChartFullScreenDialog = ({ open, onClose, title, info, children }: Props) => (
  <Dialog open={open} onOpenChange={next => !next && onClose()}>
    <DialogContent className="al-min-w-[95vw] al-min-h-[95vh] !al-z-[100]">
      <DialogHeader>
        {/* `DialogTitle` wraps only the heading text so the dialog's accessible
            name stays concise; the caption is a separate focusable control
            alongside it, not nested inside the h2. */}
        <Stack direction="row" align="center" gap="xs">
          <DialogTitle>{title}</DialogTitle>
          {info ? (
            // Lego `IconButton` gives the trigger its 40x40 icon hit area for
            // free. Its own `title` tooltip renders at the default `al-z-50`
            // and can't take `contentProps`, so inside this z-100 dialog it is
            // always occluded; the outer `Tooltip` carries the `!al-z-[101]`
            // lift that keeps the caveat readable in full screen — the one
            // tooltip the user actually sees here.
            <Tooltip title={info} contentProps={{ className: "!al-z-[101]" }}>
              <IconButton
                title={info}
                className="al-cursor-help al-text-gray-400 hover:al-text-gray-600"
              >
                <IconInfoCircle size={16} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export default ChartFullScreenDialog;
